/**
 * BadgeIcon — the artwork for all twenty badges on the masterlist, as one component.
 *
 * ORIGIN: a generated badge sheet supplied by the designer (five groups, twenty medallions, each a
 * glyph over cupped hands holding a small figure, with an INTRO/LOW/MID/TOP banner beneath). The
 * composition and the cupped-hands device are theirs. This is a rebuild to the brief's adaptions:
 * 256x256 squares, the app palette plus one complementary colour, a star rating in place of the tier
 * banner, a real tool workshop, sharper separation between the low and middle rungs, larger text in the
 * app's own font, and no numbering.
 *
 * ── ONE COMPONENT, NOT TWENTY FILES ──────────────────────────────────────────────────────────────
 * Every badge shares a ring, a face, the hands base, a star row and an optional label; only the glyph
 * differs. Twenty standalone .svg files would mean twenty copies of that chrome to keep in step, none
 * of them able to read a design token or grey themselves out for the unearned state the panel needs.
 * So the chrome is written once and the glyphs are twenty small functions. If flat files are ever
 * wanted for a deck or Figma, they can be rendered out of this — the reverse is not true.
 *
 * ── WHERE THE RUNG COMES FROM ────────────────────────────────────────────────────────────────────
 * NOT from the sheet's banners. The sheet labels `useful` (5 adoptions) and `relied_on` (25 adoptions)
 * both MID, and `workshop` (10 published tools) MID as well, so its ladder does not separate things the
 * product genuinely separates. The masterlist migration (0025) carries the real difficulty in
 * criterion_signal + criterion_threshold, so the rung is derived from that instead, on one scale that
 * holds across all five groups:
 *
 *     1 star   a first of something, or a threshold of 1        getting started
 *     2 stars  a small multiple (3-5)                           established
 *     3 stars  a substantial count (10-25), or a real step up    serious
 *     4 stars  exceptional (100), or a scarce distinction        rare
 *     5 stars  awarded by a person                              Recognition only
 *
 * That is a judgement call and it is worth knowing it was made: it moves `relied_on` above `useful`
 * and `workshop` up to 3, which the sheet did not. The masterlist itself has no tier column, so
 * something had to decide.
 *
 * ── ACCESSIBILITY ────────────────────────────────────────────────────────────────────────────────
 * Decorative by default: aria-hidden, focusable="false". Every host that renders one of these already
 * writes the badge's name and criterion in text beside it (the profile band's slots, the picker rows,
 * the public profile's chips), so the medallion is reinforcement and nothing is lost when it does not
 * render. The star count is deliberately NOT announced: it is a visual grouping of the masterlist's own
 * thresholds, not a score, and the app is under a standing rule against badge counts and totals
 * (PR-F58, UR-F22, PC-F31). Pass `title` only where the icon stands alone.
 */
import type { CSSProperties } from 'react';
import './badges.css';

export type BadgeRung = 1 | 2 | 3 | 4 | 5;

/**
 * Every badge_key on the masterlist, with its rung. Derived from migration 0025's thresholds — see the
 * ladder in the header. Keyed by the same strings the API returns, so a key that is not here renders
 * the fallback glyph rather than nothing.
 */
const RUNG_OF: Record<string, BadgeRung> = {
  // Contributing — two separate ladders, tools published and adoptions earned.
  first_share: 1, //   1 tool approved
  toolmaker: 2, //     3 tools
  workshop: 3, //     10 tools
  first_reuse: 1, //   1 adoption
  useful: 2, //        5 adoptions
  relied_on: 3, //    25 adoptions
  indispensable: 4, // 100 adoptions
  in_the_five: 4, //  the weekly Top 5 — scarce by construction
  // Reusing — tools adopted.
  first_adopter: 1, //  1
  reuser: 2, //         5
  serial_reuser: 3, // 25
  // Helping.
  helpful_voice: 2, //     5 comments
  good_citizen: 1, //      1 reported issue solved
  checked_it_works: 2, //  5 "did it work?" answers
  answered: 2, //          5 forum posts
  // Learning.
  welcome: 1, //      first sign-in
  safe_hands: 2, //   training complete + check passed
  curious: 3, //     10 learning links
  levelled_up: 3, //  competency above the baseline you set
  // Recognition — the only human-awarded badge on the list.
  above_and_beyond: 5,
};

/** Fallback for a key not on the masterlist — see the note on RUNG_OF. */
const DEFAULT_RUNG: BadgeRung = 1;

/* ── Geometry ────────────────────────────────────────────────────────────────────────────────────────
   Always a 256x256 square, as the brief asked, and now with three things to fit rather than two: the
   medallion, the star row and the TITLE. The title on the square is a requirement, not a nicety —
   twenty medallions are hard to tell apart by glyph alone, and the tier plate answers "how hard was
   this" but never "which one is it".

   THE SQUEEZE IS REAL AND WORTH NAMING. The profile band renders this square at 64px. A medallion, a
   star row and a readable name cannot all coexist there: at r=70 the disc lands at 35px and the title at
   7.5px. So the three signals divide the work by size — the PLATE COLOUR carries the tier at any size,
   the medallion carries recognition once learnt, and the title is readable from about 120px up. If the
   band's tiles were bigger the title would read there too, but 64px is a Figma measurement, so that is a
   decision to take rather than assume.

   `labelY2` is the pair of baselines a two-line title uses; a one-liner sits on `labelY`, between them. */
const G = { cy: 86, r: 70, starY: 176, labelY: 216, labelY2: [204, 236] };
/** Without a title the medallion grows and drops to the middle of the square. */
const G_NO_LABEL = { cy: 107, r: 92, starY: 225 };

/** Available width for the title, and the character count that fits it at 30px Inter medium. */
const LABEL_MAX_CHARS = 14;

/**
 * Break a long title across two lines at the space nearest its middle, rather than shrinking it.
 * Shrinking to fit would leave "Above and Beyond" visibly smaller than "Useful" on adjacent tiles, which
 * reads as a mistake; two balanced lines read as typesetting. A single word is left alone — there is
 * nothing to break — and none of the twenty names is one long word, so that path never fires today.
 */
function labelLines(text: string): string[] {
  if (text.length <= LABEL_MAX_CHARS) return [text];
  const words = text.split(' ');
  if (words.length < 2) return [text];
  let at = 1;
  let best = Infinity;
  for (let i = 1; i < words.length; i++) {
    const diff = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
    if (diff < best) {
      best = diff;
      at = i;
    }
  }
  return [words.slice(0, at).join(' '), words.slice(at).join(' ')];
}

/**
 * THE RING IS NO LONGER A TIER SIGNAL, and the face carries no tint.
 *
 * Both used to: a thin pale ring on a plain face meant rung 1, a heavy ink ring on a tinted face meant
 * rung 3. That was the best answer available while every plate was the same sand. Now the PLATE carries
 * the rung (see badges.css), and keeping two more axes saying the same thing made the medallion muddy —
 * five ring weights and five face washes fighting five plate colours. So the ring is one consistent ink
 * edge on every badge, which is most of what makes twenty of them read as one family, and the face is a
 * clean near-white disc the glyph sits on legibly whatever colour is behind it.
 */
const RING_WIDTH = 4;

/**
 * A five-point star as a unit path (outer radius 1), built once at module load rather than typed out,
 * so the geometry is right by construction. 0.4 is the inner radius — slightly fatter than the
 * classical 0.382 because these render at 9px across in the profile band and a thin-waisted star turns
 * to mush there.
 */
const STAR_PATH = (() => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? 1 : 0.4;
    const a = ((-90 + i * 36) * Math.PI) / 180;
    pts.push(`${(Math.cos(a) * rad).toFixed(3)} ${(Math.sin(a) * rad).toFixed(3)}`);
  }
  return `M ${pts.join(' L ')} Z`;
})();

/* ── The shared chrome ───────────────────────────────────────────────────────────────────────────── */

/**
 * The cupped hands holding a small figure — on every badge, and the sheet's own unifying device. Kept
 * because it is the single thing that makes twenty different drawings read as one family, but cut down
 * to a base band: in the source it takes the bottom third of the medallion, which left the glyph — the
 * part that actually says which badge this is — fighting for room. Here it is a shallow bowl with the
 * figure's head in the notch, clipped to the medallion so its shoulders never break the ring.
 *
 * Drawn in medallion-local coordinates (origin at the circle's centre, radius 100 normalised by the
 * caller's scale), so one shape serves both geometry sets.
 */
function HandsBase() {
  return (
    <g>
      {/* The cup. Fingertips rise at the sides and the middle dips, so the figure reads as being HELD
          rather than standing on a hill — which is what the first attempt looked like. The whole mass
          sits in the bottom 30% of the medallion, not the 40% it started at, to give the glyph room. */}
      <path
        d="M -104 58 C -70 40 -36 48 -14 64 C -6 70 6 70 14 64 C 36 48 70 40 104 58 L 104 124 L -104 124 Z"
        fill="var(--badge-ink)"
      />
      {/* The figure. Its shoulders are mostly BELOW the cup's dip and only the head clears it — the
          first attempt had a tall cream dome sitting proud of the dark, which read as a headstone. */}
      <path d="M -14 80 C -14 63 -7 55 0 55 C 7 55 14 63 14 80 Z" fill="var(--badge-shell)" />
      <circle cx="0" cy="44" r="9" fill="var(--badge-lime)" />
    </g>
  );
}

/** The star row. `rung` stars in gold, centred. Purely visual — see the header on why it is silent. */
function Stars({ rung, y, scale }: { rung: BadgeRung; y: number; scale: number }) {
  const gap = 15 * scale;
  const size = 13 * scale;
  const first = -((rung - 1) * gap) / 2;
  return (
    <g fill="var(--badge-gold)">
      {Array.from({ length: rung }, (_, i) => (
        <path
          key={i}
          d={STAR_PATH}
          transform={`translate(${128 + first + i * gap} ${y}) scale(${size})`}
        />
      ))}
    </g>
  );
}

/* ── The glyphs ──────────────────────────────────────────────────────────────────────────────────────
   Each draws around its OWN origin in a box of roughly 96 x 76, and the caller translates and scales it
   into the medallion. That keeps every glyph authorable in round numbers and independent of which
   geometry set is in use.

   Colours are the three glyph tokens only (ink, accent, lime) plus shell for knockouts, so the unearned
   state can drain all of them from one place.

   WHERE THIS DEPARTS FROM THE SHEET, beyond the brief's own list: the sheet draws a dotted speech
   bubble on `first_share`, `helpful_voice` AND `answered`, so three badges from two different groups
   share a silhouette. Since a badge's whole job at 64px is to be told apart from the others,
   `first_share` becomes a tool leaving a tray (sharing outward) and the two Helping badges keep the
   bubbles. `safe_hands` and `relied_on` had the same collision with a shield each; `relied_on` keeps
   the filled shield, `safe_hands` becomes an open palm under a light shield. */

const STROKE = { fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

/** first_share — a tool lifted out of a tray: your first thing put out for others. */
function GlyphFirstShare() {
  return (
    <g>
      <path d="M -34 12 H 34 V 30 H -34 Z" fill="var(--badge-accent)" />
      <path {...STROKE} d="M 0 -34 V 4" stroke="var(--badge-ink)" strokeWidth="8" />
      <path {...STROKE} d="M -16 -18 L 0 -34 L 16 -18" stroke="var(--badge-ink)" strokeWidth="8" />
      <circle cx="0" cy="-34" r="5" fill="var(--badge-lime)" />
    </g>
  );
}

/**
 * toolmaker — a ring spanner crossed with a screwdriver. Three tools approved.
 *
 * Drawn as two rotated uprights rather than two diagonal strokes: the first attempt was a plain X with
 * a coloured dot on each arm, which is an X, not a pair of tools. Rotating a vertical tool means each
 * one can have its own real end — an open ring on the spanner, a fat handle on the screwdriver — and
 * those ends are what tell them apart at 64px.
 */
function GlyphToolmaker() {
  return (
    <g>
      <g transform="rotate(-44)">
        {/* spanner: shaft down, ring head up */}
        <rect x="-5" y="-14" width="10" height="48" rx="4" fill="var(--badge-ink)" />
        <circle cx="0" cy="-22" r="11" fill="none" stroke="var(--badge-ink)" strokeWidth="7" />
      </g>
      <g transform="rotate(44)">
        {/* screwdriver: blade down, grip up. Narrow and ribbed so it reads as a handle rather than the
            leaf-shaped blob a wide rounded rect turned into at 64px. */}
        <rect x="-4" y="-2" width="8" height="36" rx="2" fill="var(--badge-accent)" />
        <rect x="-8" y="-32" width="16" height="32" rx="5" fill="var(--badge-lime)" />
        <g stroke="var(--badge-ink)" strokeWidth="2.5" opacity="0.5">
          <path d="M -8 -24 H 8" />
          <path d="M -8 -16 H 8" />
        </g>
      </g>
    </g>
  );
}

/**
 * workshop — THE BRIEF'S FIX. The sheet drew a projector screen and an audience, i.e. a professional
 * workshop; the badge is for having ten tools approved, so it wants a tool workshop. This is a pegboard
 * with three tools hung on it over a bench.
 */
function GlyphWorkshop() {
  return (
    <g>
      {/* the pegboard */}
      <rect x="-42" y="-38" width="84" height="52" rx="5" fill="rgb(0 157 154 / 0.22)" />
      {/* Three tools, told apart by their TOPS rather than their shafts — three vertical sticks with
          different dots on them all read the same at small sizes. Claw hammer, open spanner, screwdriver. */}
      <g fill="var(--badge-ink)">
        {/* hammer */}
        <rect x="-29" y="-22" width="7" height="30" rx="2" />
        <path d="M -36 -30 H -18 V -22 H -30 L -36 -24 Z" />
        {/* open-ended spanner */}
        <rect x="-4" y="-18" width="7" height="26" rx="2" />
        <path d="M -8 -32 H 7 V -26 H 2 V -22 H -3 V -26 H -8 Z" />
        {/* screwdriver */}
        <rect x="23" y="-18" width="6" height="26" rx="2" />
      </g>
      <rect x="19" y="-32" width="14" height="16" rx="4" fill="var(--badge-lime)" />
      {/* the bench */}
      <rect x="-48" y="20" width="96" height="10" rx="5" fill="var(--badge-ink)" />
      <rect x="-40" y="30" width="8" height="16" rx="3" fill="var(--badge-accent)" />
      <rect x="32" y="30" width="8" height="16" rx="3" fill="var(--badge-accent)" />
    </g>
  );
}

/**
 * first_reuse — one arrow carrying your tool across to somebody else. The very first time.
 *
 * Deliberately a SINGLE arc where `reuser` is a closed cycle and `serial_reuser` is three: the three
 * reuse-flavoured badges sit on rungs 1, 2 and 3, and the glyphs now count the same way the rungs do.
 * The first attempt gave first_reuse the same two-arrow cycle as reuser, which made the pair tell apart
 * only by ring weight.
 */
function GlyphFirstReuse() {
  return (
    <g>
      <rect x="-44" y="6" width="30" height="30" rx="6" fill="rgb(0 157 154 / 0.3)" />
      <rect x="14" y="6" width="30" height="30" rx="6" fill="var(--badge-accent)" />
      <path
        {...STROKE}
        d="M -29 -2 C -29 -34 29 -34 29 -2"
        fill="none"
        stroke="var(--badge-ink)"
        strokeWidth="8"
      />
      <path
        {...STROKE}
        d="M 18 -12 L 30 0 L 40 -14"
        fill="none"
        stroke="var(--badge-ink)"
        strokeWidth="8"
      />
    </g>
  );
}

/** useful — a thumbs up. */
function GlyphUseful() {
  return (
    <g>
      <path
        d="M -6 -30 C 2 -30 6 -24 6 -16 V -6 H 26 C 32 -6 36 -1 34 5 L 26 26 C 24 30 20 32 16 32 H -12 V -6 C -12 -18 -12 -30 -6 -30 Z"
        fill="var(--badge-accent)"
      />
      <rect x="-30" y="-6" width="14" height="38" rx="4" fill="var(--badge-ink)" />
      <g {...STROKE} stroke="var(--badge-lime)" strokeWidth="5">
        <path d="M 12 -38 V -46" />
        <path d="M 30 -30 L 36 -36" />
      </g>
    </g>
  );
}

/** relied_on — a filled shield with a tick. Dependability. */
function GlyphReliedOn() {
  return (
    <g>
      <path
        d="M 0 -34 L 30 -22 V 4 C 30 22 16 32 0 38 C -16 32 -30 22 -30 4 V -22 Z"
        fill="var(--badge-ink)"
      />
      <path {...STROKE} d="M -13 0 L -3 11 L 15 -10" stroke="var(--badge-lime)" strokeWidth="8" />
    </g>
  );
}

/** indispensable — a cut gem. 100 adoptions. */
function GlyphIndispensable() {
  return (
    <g>
      <path d="M -34 -12 H 34 L 0 34 Z" fill="var(--badge-lime)" />
      <path d="M -34 -12 L -20 -32 H 20 L 34 -12 Z" fill="var(--badge-accent)" />
      <path
        {...STROKE}
        d="M -20 -32 L -8 -12 L 0 -32 L 8 -12 L 20 -32"
        stroke="var(--badge-shell)"
        strokeWidth="3"
      />
      <path {...STROKE} d="M -34 -12 H 34" stroke="var(--badge-shell)" strokeWidth="3" />
    </g>
  );
}

/** in_the_five — a star raised over a small group: your tool reached the weekly Top 5. */
function GlyphInTheFive() {
  return (
    <g>
      <path d={STAR_PATH} transform="translate(0 -20) scale(22)" fill="var(--badge-gold)" />
      <g fill="var(--badge-ink)">
        <circle cx="-24" cy="18" r="8" />
        <circle cx="0" cy="14" r="9" />
        <circle cx="24" cy="18" r="8" />
        <path d="M -36 38 C -36 28 -30 24 -24 24 C -18 24 -12 28 -12 38 Z" />
        <path d="M -12 38 C -12 26 -6 22 0 22 C 6 22 12 26 12 38 Z" />
        <path d="M 12 38 C 12 28 18 24 24 24 C 30 24 36 28 36 38 Z" />
      </g>
    </g>
  );
}

/** first_adopter — a basket with a tick: you took something up. */
function GlyphFirstAdopter() {
  return (
    <g>
      <path d="M -32 -6 H 32 L 26 30 H -26 Z" fill="var(--badge-accent)" />
      <path
        {...STROKE}
        d="M -16 -6 A 16 16 0 0 1 16 -6"
        stroke="var(--badge-ink)"
        strokeWidth="7"
      />
      <circle cx="20" cy="20" r="14" fill="var(--badge-ink)" />
      <path {...STROKE} d="M 13 20 L 18 26 L 27 14" stroke="var(--badge-lime)" strokeWidth="5" />
    </g>
  );
}

/** reuser — a full two-arrow cycle. Five tools adopted. */
function GlyphReuser() {
  return (
    <g {...STROKE} fill="none" strokeWidth="9">
      <circle cx="0" cy="0" r="26" stroke="rgb(0 157 154 / 0.25)" />
      <path d="M -26 0 A 26 26 0 0 1 14 -22" stroke="var(--badge-accent)" />
      <path d="M 26 0 A 26 26 0 0 1 -14 22" stroke="var(--badge-ink)" />
      <path d="M 4 -30 L 18 -20 L 6 -10" stroke="var(--badge-accent)" />
      <path d="M -4 30 L -18 20 L -6 10" stroke="var(--badge-ink)" />
    </g>
  );
}

/** serial_reuser — three arrows round the cycle. Twenty-five. */
function GlyphSerialReuser() {
  return (
    <g {...STROKE} fill="none" strokeWidth="8">
      {[0, 120, 240].map((rot, i) => (
        <g key={rot} transform={`rotate(${rot})`}>
          <path
            d="M 0 -28 A 28 28 0 0 1 24 14"
            stroke={i === 1 ? 'var(--badge-accent)' : 'var(--badge-ink)'}
          />
          <path
            d="M 14 6 L 26 16 L 14 24"
            stroke={i === 1 ? 'var(--badge-accent)' : 'var(--badge-ink)'}
          />
        </g>
      ))}
    </g>
  );
}

/** helpful_voice — a speech bubble with three dots. Comments written. */
function GlyphHelpfulVoice() {
  return (
    <g>
      <path
        d="M -34 -26 H 34 A 6 6 0 0 1 40 -20 V 10 A 6 6 0 0 1 34 16 H -6 L -20 32 V 16 H -34 A 6 6 0 0 1 -40 10 V -20 A 6 6 0 0 1 -34 -26 Z"
        fill="var(--badge-accent)"
      />
      <g fill="var(--badge-shell)">
        <circle cx="-16" cy="-5" r="5" />
        <circle cx="0" cy="-5" r="5" />
        <circle cx="16" cy="-5" r="5" />
      </g>
    </g>
  );
}

/**
 * good_citizen — a heart over two colleagues. An issue you reported got fixed, so everyone benefits.
 *
 * The figures sit ENTIRELY below the heart's widest point. The first attempt tucked them behind it and
 * only their heads showed above the top lobes, which read as horns.
 */
function GlyphGoodCitizen() {
  return (
    <g>
      <path
        d="M 0 6 C -16 -6 -27 -14 -27 -27 C -27 -38 -18 -44 -10 -44 C -4 -44 0 -40 0 -35 C 0 -40 4 -44 10 -44 C 18 -44 27 -38 27 -27 C 27 -14 16 -6 0 6 Z"
        fill="var(--badge-lime)"
      />
      <g fill="var(--badge-ink)">
        <circle cx="-22" cy="16" r="9" />
        <circle cx="22" cy="16" r="9" />
        <path d="M -36 44 C -36 32 -30 27 -22 27 C -14 27 -8 32 -8 44 Z" />
        <path d="M 8 44 C 8 32 14 27 22 27 C 30 27 36 32 36 44 Z" />
      </g>
    </g>
  );
}

/** checked_it_works — a clipboard with a tick. "Did it work?" answered five times. */
function GlyphCheckedItWorks() {
  return (
    <g>
      <rect x="-28" y="-28" width="56" height="62" rx="6" fill="var(--badge-accent)" />
      <rect x="-12" y="-36" width="24" height="14" rx="4" fill="var(--badge-ink)" />
      <path {...STROKE} d="M -13 2 L -3 13 L 15 -8" stroke="var(--badge-shell)" strokeWidth="8" />
    </g>
  );
}

/** answered — a speech bubble with a tick. Forum posts and replies. */
function GlyphAnswered() {
  return (
    <g>
      <path
        d="M -34 -26 H 34 A 6 6 0 0 1 40 -20 V 10 A 6 6 0 0 1 34 16 H 6 L -8 32 V 16 H -34 A 6 6 0 0 1 -40 10 V -20 A 6 6 0 0 1 -34 -26 Z"
        fill="var(--badge-ink)"
      />
      <path {...STROKE} d="M -14 -5 L -4 6 L 14 -15" stroke="var(--badge-lime)" strokeWidth="8" />
    </g>
  );
}

/** welcome — a roadside signboard, which is also the Kombi motif's own furniture. First sign-in. */
function GlyphWelcome() {
  return (
    <g>
      <rect x="-40" y="-30" width="80" height="36" rx="5" fill="var(--badge-accent)" />
      <g {...STROKE} stroke="var(--badge-shell)" strokeWidth="5">
        <path d="M -28 -20 H 22" />
        <path d="M -28 -8 H 4" />
      </g>
      {/* A lime chevron: the sign points you somewhere, which is what a welcome does — and it is the
          same roadside furniture the Kombi motif already uses. */}
      <path
        {...STROKE}
        d="M 14 -14 L 24 -8 L 14 -2"
        stroke="var(--badge-lime)"
        strokeWidth="5"
        fill="none"
      />
      <g {...STROKE} stroke="var(--badge-ink)" strokeWidth="7">
        <path d="M -22 6 V 32" />
        <path d="M 22 6 V 32" />
      </g>
    </g>
  );
}

/**
 * safe_hands — a safety helmet with a tick. The mandatory training done and the check passed.
 *
 * NOT a shield. `relied_on` already owns the filled-shield-with-a-tick silhouette, and the first two
 * attempts here — an outline shield, then a shield over a palm — both still read as "shield" at 64px,
 * which is two badges from different groups you cannot tell apart. A helmet says the same thing the
 * badge's NAME says, and nothing else on the sheet is helmet-shaped.
 */
function GlyphSafeHands() {
  return (
    <g>
      {/* the dome */}
      <path d="M -30 14 A 30 30 0 0 1 30 14 Z" fill="var(--badge-accent)" />
      {/* the ridge */}
      <path {...STROKE} d="M 0 -16 V 14" stroke="var(--badge-shell)" strokeWidth="5" fill="none" />
      {/* the brim */}
      <rect x="-42" y="14" width="84" height="11" rx="5.5" fill="var(--badge-ink)" />
      {/* the pass mark, sitting clear of the helmet to its upper right */}
      <circle cx="28" cy="-26" r="15" fill="var(--badge-lime)" />
      <path
        {...STROKE}
        d="M 21 -26 L 26 -20 L 35 -33"
        stroke="var(--badge-ink)"
        strokeWidth="5"
        fill="none"
      />
    </g>
  );
}

/** curious — a magnifier over a question mark. Ten learning links opened. */
function GlyphCurious() {
  return (
    <g>
      <circle cx="-4" cy="-8" r="26" fill="rgb(0 157 154 / 0.18)" />
      <circle
        {...STROKE}
        cx="-4"
        cy="-8"
        r="26"
        fill="none"
        stroke="var(--badge-ink)"
        strokeWidth="8"
      />
      <path {...STROKE} d="M 16 12 L 36 32" stroke="var(--badge-ink)" strokeWidth="10" />
      <path
        {...STROKE}
        d="M -13 -18 A 9 9 0 1 1 -4 -6 V -2"
        fill="none"
        stroke="var(--badge-accent)"
        strokeWidth="6"
      />
      <circle cx="-4" cy="8" r="4" fill="var(--badge-lime)" />
    </g>
  );
}

/** levelled_up — a summit with a flag planted. Your level above the baseline you set. */
function GlyphLevelledUp() {
  return (
    <g>
      <path d="M -40 30 L -8 -14 L 8 6 L 20 -8 L 40 30 Z" fill="var(--badge-ink)" />
      <path d="M -8 -14 L 4 2 L -20 30 H -40 Z" fill="var(--badge-accent)" />
      <path {...STROKE} d="M 20 -34 V -6" stroke="var(--badge-ink)" strokeWidth="5" />
      <path d="M 20 -34 L 42 -28 L 20 -20 Z" fill="var(--badge-lime)" />
    </g>
  );
}

/**
 * above_and_beyond — a trophy. The only badge a person hands over rather than a rule.
 *
 * A trophy rather than the star the first attempt used, for two reasons: the sheet's own Recognition
 * group mark is a trophy, and this badge already wears FIVE gold stars in its rung row — a sixth big one
 * as the glyph made the medallion read as a pile of stars instead of an award. Gold throughout, which
 * only this badge and the two rung-4s are allowed.
 */
function GlyphAboveAndBeyond() {
  return (
    <g>
      {/* handles */}
      <g {...STROKE} fill="none" stroke="var(--badge-gold)" strokeWidth="6">
        <path d="M -26 -30 C -44 -30 -44 -4 -26 -4" />
        <path d="M 26 -30 C 44 -30 44 -4 26 -4" />
      </g>
      {/* cup */}
      <path
        d="M -27 -36 H 27 V -12 C 27 6 16 17 0 17 C -16 17 -27 6 -27 -12 Z"
        fill="var(--badge-gold)"
      />
      <path d={STAR_PATH} transform="translate(0 -12) scale(11)" fill="var(--badge-shell)" />
      {/* stem and plinth */}
      <rect x="-6" y="17" width="12" height="15" fill="var(--badge-ink)" />
      <rect x="-24" y="32" width="48" height="11" rx="5" fill="var(--badge-ink)" />
    </g>
  );
}

/** A key that is not on the masterlist. A plain medallion beats a broken one. */
function GlyphFallback() {
  return <circle cx="0" cy="0" r="20" fill="var(--badge-accent)" />;
}

const GLYPH_OF: Record<string, () => JSX.Element> = {
  first_share: GlyphFirstShare,
  toolmaker: GlyphToolmaker,
  workshop: GlyphWorkshop,
  first_reuse: GlyphFirstReuse,
  useful: GlyphUseful,
  relied_on: GlyphReliedOn,
  indispensable: GlyphIndispensable,
  in_the_five: GlyphInTheFive,
  first_adopter: GlyphFirstAdopter,
  reuser: GlyphReuser,
  serial_reuser: GlyphSerialReuser,
  helpful_voice: GlyphHelpfulVoice,
  good_citizen: GlyphGoodCitizen,
  checked_it_works: GlyphCheckedItWorks,
  answered: GlyphAnswered,
  welcome: GlyphWelcome,
  safe_hands: GlyphSafeHands,
  curious: GlyphCurious,
  levelled_up: GlyphLevelledUp,
  above_and_beyond: GlyphAboveAndBeyond,
};

export interface BadgeIconProps {
  /** The masterlist `badge_key` as the API returns it. An unknown key draws the fallback. */
  badgeKey: string;
  /**
   * False draws the same medallion drained onto the ink opacity ramp, for the full panel's not-yet-
   * earned rows (PR-F44/PR-F45). Defaults true because most hosts only ever render earned badges.
   */
  earned?: boolean;
  /**
   * Draw the badge's name inside the square, in the app font. For the design sheet and anywhere the
   * medallion stands alone. OFF by default: in the app every host already writes the name in text next
   * to the icon, and a second copy inside the artwork is both redundant and, at the 64px the profile
   * band renders these at, a 6px smear.
   */
  label?: string;
  /**
   * Draw the themed square plate behind the medallion (see --badge-plate in badges.css). ON by default,
   * because the app's own hosts put these in square tiles and without it the medallion has nothing to
   * sit on. Turn it OFF where the host already provides the container — a rounded pill, say, where a
   * square plate inside it would be a sticker on a sticker. No host does today: the public profile's
   * badge pills were the one caller, and they became band tiles when that page took on /me's frame.
   */
  plate?: boolean;
  /** Sets role="img" with this as the accessible name. Only where the icon is not beside its own text. */
  title?: string;
  className?: string;
  style?: CSSProperties;
}

export function BadgeIcon({
  badgeKey,
  earned = true,
  label,
  plate = true,
  title,
  className = '',
  style,
}: BadgeIconProps) {
  const rung = RUNG_OF[badgeKey] ?? DEFAULT_RUNG;
  const Glyph = GLYPH_OF[badgeKey] ?? GlyphFallback;
  const g = label ? G : G_NO_LABEL;
  const lines = label ? labelLines(label) : [];
  /* Two baselines for a wrapped title, one centred baseline for a single line. */
  const baselines = lines.length > 1 ? G.labelY2 : [G.labelY];

  /* The glyphs are authored in a ~96x76 box around their own origin (see the glyph section), which is
     sized for a medallion of radius 100. Scaling by r/100 lets both geometry sets reuse them
     untouched. The hands base is drawn in the same normalised space. */
  const scale = g.r / 100;

  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const };

  return (
    <svg
      className={`tb-badge ${className}`.trim()}
      data-badge={badgeKey}
      data-rung={rung}
      data-earned={earned ? 'true' : 'false'}
      data-testid="badge-icon"
      viewBox="0 0 256 256"
      focusable="false"
      {...(style ? { style } : {})}
      {...a11y}
    >
      {title ? <title>{title}</title> : null}

      {/* Rung 5 only: rays behind the medallion. Recognition is the one badge a human hands over, so it
          is allowed to be the one badge that glows. */}
      {rung === 5 && (
        /* --badge-shell, not gold: rung 5's plate IS full gold, so gold rays on it are invisible. */
        <g stroke="var(--badge-shell)" strokeWidth="5" strokeLinecap="round" opacity="0.6">
          {Array.from({ length: 12 }, (_, i) => (
            <path
              key={i}
              d={`M 128 ${g.cy - g.r - 6} V ${g.cy - g.r - 20}`}
              transform={`rotate(${i * 30} 128 ${g.cy})`}
            />
          ))}
        </g>
      )}

      {/* The plate. Radius 32 of 256 is 12.5%, which lands on exactly 8px at the 64px the profile band
          renders this at — the same --radius-md the tile it sits in already uses, so the two edges agree
          instead of nearly agreeing. */}
      {plate ? (
        <rect x="0" y="0" width="256" height="256" rx="32" fill="var(--badge-plate)" />
      ) : null}
      {/* The halo: a near-white ring a little wider than the medallion, so the disc lifts off the tier
          colour rather than sitting flush on it. Only meaningful when there is a plate to lift off. */}
      {plate ? <circle cx="128" cy={g.cy} r={g.r + 9} fill="var(--badge-halo)" /> : null}

      {/* The face — one clean near-white disc, no rung tint (see RING_WIDTH's note). Everything is then
          clipped to it, and the ring goes on top so its stroke is never covered. */}
      <clipPath id={`badge-clip-${badgeKey}`}>
        <circle cx="128" cy={g.cy} r={g.r} />
      </clipPath>
      <circle cx="128" cy={g.cy} r={g.r} fill="var(--badge-shell)" />

      <g clipPath={`url(#badge-clip-${badgeKey})`}>
        <g transform={`translate(128 ${g.cy}) scale(${scale})`}>
          <HandsBase />
        </g>
        {/* The glyph sits above the cup. -0.22r rather than -0.3r, and 15% larger: with the base cut
            back there is room, and at 64px a glyph that does not fill its medallion is a smudge. */}
        <g transform={`translate(128 ${g.cy - g.r * 0.22}) scale(${scale * 1.15})`}>
          <Glyph />
        </g>
      </g>

      <circle
        cx="128"
        cy={g.cy}
        r={g.r - RING_WIDTH / 2}
        fill="none"
        stroke="var(--badge-ink)"
        strokeWidth={RING_WIDTH}
      />

      <Stars rung={rung} y={g.starY} scale={scale} />

      {lines.map((line, i) => (
        <text
          key={line}
          className="tb-badge__label"
          x="128"
          y={baselines[i] ?? G.labelY}
          textAnchor="middle"
        >
          {line}
        </text>
      ))}
    </svg>
  );
}

/** The masterlist keys in sort order, for the design preview. Not used by the app. */
export const BADGE_KEYS = Object.keys(RUNG_OF);
